import { useContext, useEffect, useMemo, useState } from 'react';
import { OrganisationContext, UserContext } from '../context';
import { GET_ORGANISATION_USERS } from '../queries';
import { useFeature } from './useFeature';
import { useQuery } from './useQuery';

export const useBlockedProfileTypes = (): string[] => {
  const organisation = useContext(OrganisationContext);
  const user = useContext(UserContext);
  const [blockedTypes, setBlockedTypes] = useState<string[]>([]);

  // Feature flag: when disabled, behave as if there are no blocked profile types
  const blockedProfilesFeatureEnabled = useFeature('profiles.blocked_profile_types');

  const usersQuery = useQuery(GET_ORGANISATION_USERS, {
    selector: 'organisations[0].users',
    variables: {
      organisationKey: organisation?.key,
    },
    // Skip entirely if feature is disabled or we don't have org/user yet
    skip: !blockedProfilesFeatureEnabled || !organisation?.key || !user?.id,
    fetchPolicy: 'network-only', // Changed from 'cache-and-network' to force fresh data
    notifyOnNetworkStatusChange: true,
  });
  
  // Also query without selector to see the raw response and get currentUserBlockedProfileTypes
  const usersQueryRaw = useQuery(GET_ORGANISATION_USERS, {
    variables: {
      organisationKey: organisation?.key,
    },
    skip: !blockedProfilesFeatureEnabled || !organisation?.key || !user?.id,
    fetchPolicy: 'network-only', // Force fresh data
    notifyOnNetworkStatusChange: true,
  });
  
  useEffect(() => {
    if (!blockedProfilesFeatureEnabled) {
      if (blockedTypes.length) {
        setBlockedTypes([]);
      }
      return;
    }

    if (usersQuery.loading) {
      return;
    }
    
    if (usersQuery.error) {
      setBlockedTypes([]);
      return;
    }
    
    if (!usersQuery.data) {
      setBlockedTypes([]);
      return;
    }
    
    if (!user?.id) {
      setBlockedTypes([]);
      return;
    }
    
    const users = usersQuery.data;
    
    if (!Array.isArray(users)) {
      setBlockedTypes([]);
      return;
    }
    
    if (users.length === 0) {
      const rawData = usersQueryRaw?.data;
      const org = rawData?.organisations?.[0];
      const directBlockedTypes = org?.currentUserBlockedProfileTypes;
      
      if (Array.isArray(directBlockedTypes) && directBlockedTypes.length > 0) {
        setBlockedTypes(directBlockedTypes);
        return;
      }
    
      setBlockedTypes([]);
      return;
    }
    
    const currentUser = users.find((u: any) => u.id === user.id);
    
    if (!currentUser) {
      setBlockedTypes([]);
      return;
    }
    
    const blocked = currentUser.blockedProfileTypes;
    
    let result: string[] = [];
    if (Array.isArray(blocked)) {
      result = blocked;
    } else if (blocked === null || blocked === undefined) {
      result = [];
    } else {
      console.warn('blockedProfileTypes is not an array, got:', typeof blocked, blocked);
      result = [];
    }
    setBlockedTypes(result);
  }, [
    blockedProfilesFeatureEnabled,
    usersQuery.data,
    usersQuery.loading,
    usersQuery.error,
    user?.id,
  ]);

  return blockedTypes;
};

export const useIsProfileTypeBlocked = (profileTypeKey: string): boolean => {
  const organisation = useContext(OrganisationContext);
  const blockedProfileTypes = useBlockedProfileTypes();
  const blockedProfilesFeatureEnabled = useFeature('profiles.blocked_profile_types');

  return useMemo(() => {
    if (!blockedProfilesFeatureEnabled) return false;

    if (!blockedProfileTypes.length || !profileTypeKey) return false;
    
    if (blockedProfileTypes.includes(profileTypeKey)) return true;
    
    const profileType = organisation?.scheme?.getProfileType(profileTypeKey);
    if (profileType?.parent && blockedProfileTypes.includes(profileType.parent)) {
      return true;
    }
    
    return false;
  }, [blockedProfileTypes, profileTypeKey, organisation]);
};

